import {
  LIVE_TOPICS,
  type LivePing,
  type LiveTopic,
  livePingSchema,
  type WebRuntimeConfig,
} from '@bnb/contracts';
import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * Live sync: after a write commits, the API publishes a tiny "this changed" ping (no data).
 * We invalidate the matching TanStack Query caches and the screens refetch.
 *   local (sse)    → EventSource on /api/live (served by the dev API)
 *   AWS (appsync)  → AppSync Events WebSocket via aws-amplify (lib/live-appsync.ts)
 * Pings are a bonus: queries also refetch on focus/reconnect, so a missed ping only delays.
 */

/**
 * Which tRPC query families each topic touches. tRPC query keys look like
 * [['barnLog','recent'], {input, type}]; a key of [['barnLog']] prefix-matches all of them.
 * ADD A LINE HERE when a new router starts publishing a topic.
 */
const TOPIC_PATHS: Record<LiveTopic, string[][]> = {
  'barn-log': [['barnLog']],
  barns: [['barns']],
  weather: [['system']],
  system: [['system']],
  integrations: [['integrations']],
};

export function queryKeysForTopic(topic: LiveTopic): QueryKey[] {
  return TOPIC_PATHS[topic].map((path) => [path]);
}

/** Validate a raw ping and invalidate its queries. Returns the ping, or null if it was junk. */
export function applyPing(queryClient: QueryClient, raw: unknown): LivePing | null {
  const parsed = livePingSchema.safeParse(raw);
  if (!parsed.success) return null;
  for (const queryKey of queryKeysForTopic(parsed.data.topic)) {
    void queryClient.invalidateQueries({ queryKey });
  }
  return parsed.data;
}

/** After a reconnect we may have missed pings: refresh everything live-backed. */
export function invalidateAllLiveTopics(queryClient: QueryClient): void {
  for (const topic of LIVE_TOPICS) {
    for (const queryKey of queryKeysForTopic(topic))
      void queryClient.invalidateQueries({ queryKey });
  }
}

export type LiveState = 'off' | 'connecting' | 'live' | 'reconnecting';

/** A running transport. `close` must be idempotent. */
export interface LiveTransport {
  close(): void;
}

export interface TransportCallbacks {
  onPing(raw: unknown): void;
  onState(state: LiveState): void;
}

export type TransportFactory = (callbacks: TransportCallbacks) => LiveTransport;

/** EventSource transport for the local dev API. EventSource reconnects by itself. */
export function sseTransport(url: string, EventSourceImpl?: typeof EventSource): TransportFactory {
  return ({ onPing, onState }) => {
    onState('connecting');
    // Resolved lazily so importing this module never needs a browser.
    const Impl = EventSourceImpl ?? globalThis.EventSource;
    const source = new Impl(url);
    let everOpened = false;
    const handle = (event: MessageEvent<string>) => {
      try {
        onPing(JSON.parse(event.data));
      } catch {
        // ignore malformed frames
      }
    };
    source.onopen = () => {
      onState('live');
      everOpened = true;
    };
    source.onerror = () => {
      onState(everOpened ? 'reconnecting' : 'connecting');
    };
    // Accept both unnamed frames and `event: ping` frames.
    source.addEventListener('message', handle);
    source.addEventListener('ping', handle as EventListener);
    return {
      close() {
        source.close();
      },
    };
  };
}

export interface LiveConnection {
  /** Keep the connection open while at least one screen needs it. Returns a release function. */
  retain(): () => void;
  getState(): LiveState;
  subscribe(listener: () => void): () => void;
}

/**
 * Ref-counted live connection: opens on the first retain(), closes shortly after the last
 * release (the grace period avoids a reconnect when moving between /crew and the office shell).
 */
export function createLiveConnection(
  queryClient: QueryClient,
  factory: TransportFactory | null,
  options: { closeDelayMs?: number } = {},
): LiveConnection {
  const closeDelayMs = options.closeDelayMs ?? 5_000;
  const listeners = new Set<() => void>();
  let state: LiveState = 'off';
  let transport: LiveTransport | null = null;
  let refs = 0;
  let closeTimer: ReturnType<typeof setTimeout> | null = null;
  let wasLive = false;

  const setState = (next: LiveState) => {
    if (next === 'live' && wasLive && state !== 'live') invalidateAllLiveTopics(queryClient);
    if (next === 'live') wasLive = true;
    if (next === state) return;
    state = next;
    for (const l of listeners) l();
  };

  const open = () => {
    if (transport || !factory) return;
    transport = factory({ onPing: (raw) => applyPing(queryClient, raw), onState: setState });
  };

  const close = () => {
    transport?.close();
    transport = null;
    wasLive = false;
    setState('off');
  };

  return {
    retain() {
      refs += 1;
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
      open();
      let released = false;
      return () => {
        if (released) return;
        released = true;
        refs -= 1;
        if (refs === 0) closeTimer = setTimeout(close, closeDelayMs);
      };
    },
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/** Pick the transport for this stage from /config.json. */
export function transportForConfig(config: WebRuntimeConfig): TransportFactory | null {
  const live = config.live;
  if (live.mode === 'off') return null;
  if (live.mode === 'sse') return sseTransport(live.url);
  if (config.auth.mode !== 'cognito') return null; // AppSync subscriptions need a Cognito token
  return (callbacks) => {
    let closed = false;
    let inner: LiveTransport | null = null;
    callbacks.onState('connecting');
    import('./live-appsync.ts')
      .then(({ appSyncTransport }) => {
        if (closed) return;
        inner = appSyncTransport(config, live)(callbacks);
      })
      .catch(() => callbacks.onState('reconnecting'));
    return {
      close() {
        closed = true;
        inner?.close();
      },
    };
  };
}
